import { computed } from "@vue/composition-api";
import { useVuelidate } from "@vuelidate/core";
import { between, maxValue, minLength } from "@vuelidate/validators";
import { withPastDate, withValidDate } from "@Shared/@Utils/validation-rules";
import { minValueNotRequired } from "@Shared/@Utils/new-validation-rules";
import { AddressRulesWithoutPreaproval } from "./goalsPageValidator";
import { getValidationRulesFromMap } from "@Client/@Composables/application-standalone/validations/getValidationRulesFromMap";
import { useApplicationStandaloneStoreGetters } from "@Client/@Modules/application-standalone/store/application-standalone-helpers";
import * as ApplicationStandAloneGetters from "@Client/@Modules/application-standalone/store/application-standalone-getters";
import { useSubjectPropertyFieldBindings } from "../pages/subject-property/useSubjectPropertyFieldBindings";
import { useSummaryHelpers } from "@Client/@Composables/application-standalone/pages/summary/useSummaryHelpers";
export const subjectPropertyValidator = ({
  lazy = false,
  scope = true
} = {}) => {
  const {
    propertyDetailsPurchasePrice,
    propertyDetailsOccupancy,
    propertyDetailsPurchaseDate,
    propertyDetailsLivingSpace,
    propertyDetailsAppraisedValue,
    propertyDetailsAppraisalDate,
    propertyDetailsBlock,
    propertyDetailsConcTownship,
    propertyDetailsEstimatedValue,
    propertyDetailsLot,
    propertyDetailsMlsListing,
    propertyDetailsMonthlyIncome,
    propertyDetailsRentalProperty,
    propertyStylePropertyStyle,
    propertyStylePropertyType,
    propertyStyleGarageSize,
    propertyStyleConstructionType,
    propertyStyleEnvironmentalHazard,
    propertyStyleHeat,
    propertyStyleLotSize,
    propertyStyleGarageStyle,
    propertyStyleLotSizeType,
    propertyStyleNumberOfUnits,
    propertyStyleSewageInfo,
    propertyStyleStructureAge,
    propertyStyleTenure,
    propertyStyleWaterInfo,
    propertyAddress,
    propertyCostsFeesIncludeHeat,
    propertyCostsHeating,
    propertyCostsHydro,
    propertyCostsInsurance,
    propertyCostsInterest,
    propertyCostsMaintenanceFees,
    propertyCostsManagement,
    propertyCostsOthers,
    propertyCostsPercOfTotalMaintFees,
    propertyCostsPropertyTaxes,
    propertyCostsRentalIncomeOptions,
    propertyCostsRentalIncomeOptionsPercent,
    propertyCostsRepairs,
    propertyCostsTaxResponsible,
    propertyCostsTaxYear
  } = useSubjectPropertyFieldBindings();
  const {
    GET_SCHEMA_RULES_BY_PATH,
    PROPERTY_REQUESTED,
    HAS_CONSOLIDATED_REQUEST
  } = useApplicationStandaloneStoreGetters([ApplicationStandAloneGetters.GET_SCHEMA_RULES_BY_PATH, ApplicationStandAloneGetters.HAS_CONSOLIDATED_REQUEST, ApplicationStandAloneGetters.PROPERTY_REQUESTED]);
  const {
    currentFlow
  } = useSummaryHelpers();
  const subjectPropertyRules = getSubjectPropertyRules(currentFlow.value);
  const rules = computed(() => {
    return getValidationRulesFromMap(() => !HAS_CONSOLIDATED_REQUEST.value || PROPERTY_REQUESTED.value, GET_SCHEMA_RULES_BY_PATH, subjectPropertyRules);
  });
  const v$ = useVuelidate(rules, {
    propertyDetailsPurchasePrice,
    propertyDetailsOccupancy,
    propertyDetailsPurchaseDate,
    propertyDetailsLivingSpace,
    propertyDetailsAppraisedValue,
    propertyDetailsAppraisalDate,
    propertyDetailsBlock,
    propertyDetailsConcTownship,
    propertyDetailsEstimatedValue,
    propertyDetailsLot,
    propertyDetailsMlsListing,
    propertyDetailsMonthlyIncome,
    propertyDetailsRentalProperty,
    propertyStylePropertyStyle,
    propertyStylePropertyType,
    propertyStyleGarageSize,
    propertyStyleConstructionType,
    propertyStyleEnvironmentalHazard,
    propertyStyleHeat,
    propertyStyleLotSize,
    propertyStyleGarageStyle,
    propertyStyleLotSizeType,
    propertyStyleNumberOfUnits,
    propertyStyleSewageInfo,
    propertyStyleStructureAge,
    propertyStyleTenure,
    propertyStyleWaterInfo,
    propertyAddress,
    propertyCostsFeesIncludeHeat,
    propertyCostsHeating,
    propertyCostsHydro,
    propertyCostsInsurance,
    propertyCostsInterest,
    propertyCostsMaintenanceFees,
    propertyCostsManagement,
    propertyCostsOthers,
    propertyCostsPercOfTotalMaintFees,
    propertyCostsPropertyTaxes,
    propertyCostsRentalIncomeOptions,
    propertyCostsRentalIncomeOptionsPercent,
    propertyCostsRepairs,
    propertyCostsTaxResponsible,
    propertyCostsTaxYear
  }, {
    $scope: scope,
    $lazy: lazy
  });
  return {
    v$
  };
};
const getSubjectPropertyRules = currentFlow => {
  return {
    propertyDetailsOccupancy: {},
    propertyDetailsPurchaseDate: {
      withPastDate: () => {
        return currentFlow !== "purchase-approval" ? withPastDate : true;
      },
      withValidDate: () => withValidDate
    },
    propertyDetailsLivingSpace: {},
    propertyDetailsAppraisedValue: {
      minValue: ({
        isRequired
      }) => minValueNotRequired(1000, isRequired),
      maxValue: maxValue(1000000000000)
    },
    propertyDetailsAppraisalDate: {
      withValidDate: () => withValidDate
    },
    propertyDetailsBlock: {},
    propertyDetailsConcTownship: {},
    propertyDetailsEstimatedValue: {
      minValue: ({
        isRequired
      }) => minValueNotRequired(1000, isRequired),
      maxValue: maxValue(1000000000000)
    },
    propertyDetailsLot: {},
    propertyDetailsMlsListing: {},
    propertyDetailsMonthlyIncome: {
      between: between(0, 1000000000000)
    },
    propertyDetailsRentalProperty: {},
    propertyAddress: AddressRulesWithoutPreaproval,
    propertyStylePropertyStyle: {},
    propertyStyleConstructionType: {},
    propertyStyleEnvironmentalHazard: {},
    propertyStyleHeat: {},
    propertyStyleLotSize: {},
    propertyStyleGarageStyle: {},
    propertyStyleLotSizeType: {},
    propertyStyleNumberOfUnits: {},
    propertyStyleSewageInfo: {},
    propertyStyleStructureAge: {},
    propertyStyleTenure: {},
    propertyStyleWaterInfo: {},
    propertyStylePropertyType: {},
    propertyStyleGarageSize: {},
    propertyCostsFeesIncludeHeat: {},
    propertyCostsHeating: {
      between: between(0, 1000000000000)
    },
    propertyCostsHydro: {
      between: between(0, 1000000000000)
    },
    propertyCostsInsurance: {
      between: between(0, 1000000000000)
    },
    propertyCostsInterest: {
      between: between(0, 1000000000000)
    },
    propertyCostsMaintenanceFees: {
      between: between(0, 1000000000000)
    },
    propertyCostsManagement: {
      between: between(0, 1000000000000)
    },
    propertyCostsOthers: {
      between: between(0, 1000000000000)
    },
    propertyCostsPercOfTotalMaintFees: {},
    propertyCostsPropertyTaxes: {
      between: between(1, 1000000000000)
    },
    propertyCostsRentalIncomeOptions: {},
    propertyCostsRentalIncomeOptionsPercent: {},
    propertyCostsRepairs: {
      between: between(0, 1000000000000)
    },
    propertyCostsTaxResponsible: {},
    propertyCostsTaxYear: {
      validYear: minLength(4)
    }
  };
};